import {
  type ChannelMarkupConditionType,
  validateChannelMarkupConditionValue,
} from '@luxuryescapes/contract-svc-promo/channelMarkup';
import { isPriceConditionType } from '@luxuryescapes/contract-svc-promo/channelMarkup/admin/v2';

export { isPriceConditionType };

// The contract exports the guard but not the type it narrows to. The `string` parameter and constrained `infer`
// keep this resolving whether or not `strictFunctionTypes` is enabled.
export type PriceConditionType = typeof isPriceConditionType extends ((
  value: string,
) => value is infer T extends string)
  ? T
  : never;

/** The editable pieces of a price condition value such as `INSIDE-RANGE:100-500` or `LOWER-THAN:2500`. */
export type PriceConditionParts = {
  mode: string;
  min: string;
  max: string;
};

const PREFIXED_VALUE_PATTERN = /^([A-Za-z-]+)\s*[:\s]\s*(.*)$/;
const AMOUNTS_PATTERN = /^([^-]*?)\s*(?:-\s*(.*))?$/;

// How the contract reads a band rule saved with only one amount: `INSIDE-RANGE:400` is above 400 and
// `OUTSIDE-RANGE:400` is below 400.
const ONE_SIDED_BAND_MODES: Record<string, string> = {
  'INSIDE-RANGE': 'HIGHER-THAN',
  'OUTSIDE-RANGE': 'LOWER-THAN',
};
// The contract reads legacy unprefixed values as `INSIDE-RANGE` (`100-500`) or `HIGHER-THAN` (`400`).
const LEGACY_BAND_MODE = 'INSIDE-RANGE';
const LEGACY_THRESHOLD_MODE = 'HIGHER-THAN';

function findMode(modes: ReadonlyArray<string>, name: string | undefined): string | undefined {
  return modes.find((mode) => mode.toUpperCase() === name?.toUpperCase());
}

/**
 * Splits a stored price condition value into its rule and amounts, keeping the meaning the contract gives it:
 * a one-sided `INSIDE-RANGE`/`OUTSIDE-RANGE` opens as the matching threshold rule, and legacy unprefixed
 * values (`100-500` or `400`) open as a band or an "above" threshold. An empty value uses the first rule in `modes`.
 */
export function parsePriceConditionValue(value: string, modes: ReadonlyArray<string>): PriceConditionParts {
  const trimmed = value.trim();
  const prefixedMatch = trimmed.match(PREFIXED_VALUE_PATTERN);
  const prefixedMode = findMode(modes, prefixedMatch?.[1]);
  const amounts = prefixedMode ? (prefixedMatch?.[2] ?? '') : trimmed;
  const amountsMatch = amounts.match(AMOUNTS_PATTERN);
  const min = amountsMatch?.[1]?.trim() ?? amounts;
  const max = amountsMatch?.[2]?.trim() ?? '';
  const isOneSided = min !== '' && max === '';

  let mode = prefixedMode;
  if (mode && isOneSided) {
    mode = findMode(modes, ONE_SIDED_BAND_MODES[mode.toUpperCase()]) ?? mode;
  } else if (!mode && min !== '') {
    mode = findMode(modes, isOneSided ? LEGACY_THRESHOLD_MODE : LEGACY_BAND_MODE);
  }

  return { mode: mode ?? modes[0] ?? '', min, max };
}

/**
 * Whether a rule takes a min-max band rather than a single threshold. Asked of the contract validator,
 * which only accepts `min-max` amounts for band rules, so new rules need no admin change.
 */
export function isRangePriceConditionMode(mode: string, conditionType: ChannelMarkupConditionType): boolean {
  return validateChannelMarkupConditionValue(`${mode}:1-2`, { conditionType, allowedConditionValues: [] });
}

/** Joins the parts back into the stored `RULE:min` or `RULE:min-max` form; empty until a min is entered. */
export function buildPriceConditionValue(parts: PriceConditionParts, isRange: boolean): string {
  const min = parts.min.trim();

  if (!parts.mode || min === '') {
    return '';
  }

  return isRange ? `${parts.mode}:${min}-${parts.max.trim()}` : `${parts.mode}:${min}`;
}
